import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { byId, isAr, message, copyText, saveBlob, trackEvent } from './extra-common.js';
import { showConfirm } from '../core/modal.js';

marked.setOptions({ gfm: true, breaks: true, async: false });

const STORAGE_KEY = 'kazakan_md_content';
const STORAGE_VIEW_KEY = 'kazakan_md_view';
const STORAGE_WIDE_KEY = 'kazakan_md_wide';

const ALERT_META = {
  NOTE: {
    title_ar: 'ملاحظة',
    title_en: 'Note',
    icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>'
  },
  TIP: {
    title_ar: 'تلميح',
    title_en: 'Tip',
    icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/></svg>'
  },
  IMPORTANT: {
    title_ar: 'هام',
    title_en: 'Important',
    icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>'
  },
  WARNING: {
    title_ar: 'تحذير',
    title_en: 'Warning',
    icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>'
  },
  CAUTION: {
    title_ar: 'تنبيه شديد',
    title_en: 'Caution',
    icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86 7.86 2"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>'
  }
};

const SAMPLE_MD_AR = `# مرحباً بك في محرر ومعاين Markdown المتقدم 🚀

أداة متطورة وسريعة تعمل **محلياً 100% داخل متصفحك** مع دعم شامل لكافة ميزات Markdown و GitHub Flavored Markdown (GFM).

---

> [!NOTE]
> هذا المحرر يعمل محلياً في جهازك بدون أي خوادم خارجية؛ مما يضمن الخصوصية والأمان التام لكافة مستنداتك وبياناتك.

> [!TIP]
> يمكنك تفعيل **ملء الشاشة** أو **توسيع العرض** أو التبديل بين أوضاع العرض (مقسم، محرر فقط، معاينة فقط) عبر الأزرار بالأعلى!

> [!WARNING]
> لا تنسَ حفظ أو تنزيل نسختك قبل إغلاق المتصفح عبر زر التنزيل \`.md\` أو كصفحة HTML كاملة.

---

### جدول مقارنة الميزات 📊

| الميزة التقنية | الدعم | السرعة والأداء |
| :--- | :---: | ---: |
| المعاينة الفورية المباشرة | مدعوم بالكامل | فائقة وفورية (Real-time) |
| وضع ملء الشاشة والشاشة العريضة | نعم | حرية كاملة للمساحة |
| تنبيهات GitHub Callouts | 5 أنواع | مظهر عصري وأنيق |
| مربعات المهام التفاعلية | تفاعلية | تحديث فوري داخل المحرر |
| كتل الأكواد البرمجية | نسخ سريع | مع تمييز اسم اللغة |

---

### قائمة المهام التفاعلية (Checklist) ✅
- [x] تجربة التوسيع بعرض الشاشة وملء الشاشة
- [x] فحص تنبيهات الملاحظات والتحذيرات
- [ ] تجربة النقر على مربعات الاختيار في المعاينة لتحديث المحرر فورياً!
- [ ] نسخ الكود البرمجي بضغطة زر واحدة

---

### كتل برمجية متقدمة 💻

\`\`\`javascript
// كود جافاسكريبت تجريبي
function calculateSpeed(words, minutes) {
  const wpm = Math.round(words / minutes);
  console.log(\`سرعة الكتابة: \${wpm} كلمة/دقيقة\`);
  return wpm;
}

calculateSpeed(450, 3);
\`\`\`

\`\`\`python
# كود بايثون مع زر نسخ فوري
def greet(name: str) -> str:
    return f"أهلاً بك، {name}! نتمنى لك تجربة ممتعة مع أدوات كازاكان."

print(greet("المطور"))
\`\`\`

---

### تنسيقات متقدمة إضافية ✨
* **نص غامق** و *نص مائل* و ~~نص مشطوب~~ و ==نص مميز باللون الأصفر==.
* روابط سريعة: [موقع كازاكان الرسمي](https://www.kazakan.com)
* اختصارات الكيبورد: اضغط على <kbd>Ctrl</kbd> + <kbd>B</kbd> للخط العريض أو <kbd>Ctrl</kbd> + <kbd>I</kbd> للخط المائل.
* اقتباسات متعددة:
  > "البساطة هي قمة التطور والاحترافية." — ليوناردو دافنشي
`;

const SAMPLE_MD_EN = `# Welcome to the Advanced Markdown Editor & Previewer 🚀

A high-performance, client-side Markdown suite running **100% locally in your browser** with full GitHub Flavored Markdown (GFM) support.

---

> [!NOTE]
> All processing and rendering happen locally on your device with zero server uploads, ensuring complete privacy and speed.

> [!TIP]
> Use the **Fullscreen** or **Wide View** toggles, or switch between Split, Editor-only, and Preview-only modes using the top bar.

> [!WARNING]
> Remember to download or copy your work before closing your browser using the \`.md\` or standalone HTML export buttons.

---

### Feature Comparison Table 📊

| Feature | Support | Performance |
| :--- | :---: | ---: |
| Real-time Instant Preview | Full Support | Zero-latency instant |
| Fullscreen & Wide Screen Modes | Yes | Distraction-free space |
| GitHub Alert Callouts | 5 Types | Native sleek styling |
| Interactive Task Checklists | Clickable | Syncs directly with editor |
| Code Blocks with Copy | One-click | Automatic language badges |

---

### Interactive Task Checklist ✅
- [x] Test Fullscreen and Wide View modes
- [x] Inspect GitHub Alert callouts
- [ ] Click the checkboxes in the live preview to toggle them in the editor!
- [ ] Copy code snippets with the one-click copy button

---

### Code Blocks with Syntax Headers 💻

\`\`\`javascript
// JavaScript sample snippet
function calculateMetrics(words, characters) {
  const readingTime = Math.ceil(words / 200);
  return { words, characters, readingTime };
}

console.log(calculateMetrics(320, 1850));
\`\`\`

\`\`\`python
# Python snippet with instant copy
def welcome(user: str) -> str:
    return f"Welcome, {user}! Enjoy writing in Markdown."

print(welcome("Developer"))
\`\`\`

---

### Additional Rich Formatting ✨
* **Bold text**, *italic text*, ~~strikethrough~~, and ==highlighted text==.
* Fast links: [Kazakan Official Store](https://www.kazakan.com)
* Keyboard shortcuts: Press <kbd>Ctrl</kbd> + <kbd>B</kbd> for bold or <kbd>Ctrl</kbd> + <kbd>I</kbd> for italic.
* Quotes:
  > "Simplicity is the ultimate sophistication." — Leonardo da Vinci
`;

const PURIFY_CONFIG = {
  USE_PROFILES: { html: true },
  ADD_TAGS: ['input', 'mark', 'kbd', 'del', 's', 'button', 'svg', 'path', 'circle', 'line', 'polyline', 'rect', 'polygon'],
  ADD_ATTR: [
    'type', 'disabled', 'checked', 'class', 'id', 'dir', 'align',
    'data-action', 'data-copy-code', 'aria-label', 'aria-hidden',
    'width', 'height', 'viewBox', 'fill', 'stroke', 'stroke-width'
  ],
  FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick']
};

const app = byId('markdown-preview-app');
const mpInput = byId('mp-input');
const mpPreview = byId('mp-preview');
const mpStatus = byId('mp-status');
const mpToggleWide = byId('mp-toggle-wide');
const mpToggleFullscreen = byId('mp-toggle-fullscreen');
const mpFileInput = byId('mp-file-input');
const mpSyncScroll = byId('mp-sync-scroll');
const mpAlertDropdown = byId('mp-alert-dropdown');
const mpAlertMenuBtn = byId('mp-alert-menu-btn');
const paneEditor = byId('mp-pane-editor');
const btnCollapseEditor = byId('mp-btn-collapse-editor');
const btnUndo = byId('mp-btn-undo');
const btnRedo = byId('mp-btn-redo');

// History Stack for Undo / Redo
const undoStack = [];
const redoStack = [];
const MAX_HISTORY = 80;
let isHistoryOperation = false;
let historyDebounceTimer = 0;
let lastRecordedValue = '';

function updateUndoRedoUI() {
  if (btnUndo) btnUndo.disabled = undoStack.length === 0;
  if (btnRedo) btnRedo.disabled = redoStack.length === 0;
}

function pushSnapshotBeforeChange() {
  if (isHistoryOperation || !mpInput) return;
  const val = mpInput.value;
  clearTimeout(historyDebounceTimer);
  if (undoStack.length === 0 || undoStack[undoStack.length - 1] !== val) {
    undoStack.push(val);
    if (undoStack.length > MAX_HISTORY) undoStack.shift();
  }
  lastRecordedValue = val;
  redoStack.length = 0;
  updateUndoRedoUI();
}

function recordHistorySnapshot() {
  if (isHistoryOperation || !mpInput) return;
  clearTimeout(historyDebounceTimer);
  historyDebounceTimer = setTimeout(() => {
    if (isHistoryOperation || !mpInput) return;
    const val = mpInput.value;
    if (val !== lastRecordedValue) {
      undoStack.push(lastRecordedValue);
      if (undoStack.length > MAX_HISTORY) undoStack.shift();
      lastRecordedValue = val;
      redoStack.length = 0;
      updateUndoRedoUI();
    }
  }, 350);
}

function doUndo() {
  if (!mpInput || undoStack.length === 0) return;
  clearTimeout(historyDebounceTimer);
  isHistoryOperation = true;
  redoStack.push(mpInput.value);
  const prev = undoStack.pop();
  mpInput.value = prev;
  lastRecordedValue = prev;
  render();
  updateUndoRedoUI();
  isHistoryOperation = false;
  message('mp-status', isAr ? 'تراجع' : 'Undo', 'info');
}

function doRedo() {
  if (!mpInput || redoStack.length === 0) return;
  clearTimeout(historyDebounceTimer);
  isHistoryOperation = true;
  undoStack.push(mpInput.value);
  const next = redoStack.pop();
  mpInput.value = next;
  lastRecordedValue = next;
  render();
  updateUndoRedoUI();
  isHistoryOperation = false;
  message('mp-status', isAr ? 'إعادة' : 'Redo', 'info');
}

if (btnUndo) btnUndo.addEventListener('click', doUndo);
if (btnRedo) btnRedo.addEventListener('click', doRedo);

let timer = 0;
let hasStarted = false;
let isScrolling = false;

// 1. Transform Markdown with Alerts, Highlights & Code Card Headers
function transformMarkdown(rawInput) {
  let text = rawInput || '';
  // Highlight ==text==
  text = text.replace(/==([^=\n]+)==/g, '<mark class="md-highlight">$1</mark>');

  let html = marked.parse(text);

  // Transform GitHub Alerts
  html = html.replace(/<blockquote>\s*<p>\s*\[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION)\](?:\s*<br\s*\/?>)?([\s\S]*?)<\/blockquote>/gi, (match, type, content) => {
    const key = type.toUpperCase();
    const meta = ALERT_META[key] || ALERT_META.NOTE;
    const title = isAr ? meta.title_ar : meta.title_en;
    let cleanContent = content.trim();
    if (cleanContent.endsWith('</p>')) {
      cleanContent = cleanContent.slice(0, -4);
    }
    return `
      <div class="md-alert md-alert-${key.toLowerCase()}">
        <div class="md-alert-header">
          <span class="md-alert-icon" aria-hidden="true">${meta.icon}</span>
          <strong class="md-alert-title">${title}</strong>
        </div>
        <div class="md-alert-body"><p>${cleanContent}</p></div>
      </div>
    `;
  });

  // Transform Code Blocks with Card & Copy Button
  html = html.replace(/<pre><code(?:\s+class="language-([a-zA-Z0-9_-]+)")?>([\s\S]*?)<\/code><\/pre>/gi, (match, langCode, codeContent) => {
    const displayLang = (langCode || 'text').toUpperCase();
    const copyLabel = isAr ? 'نسخ' : 'Copy';
    return `
      <div class="md-code-card">
        <div class="md-code-header">
          <span class="md-code-lang">${displayLang}</span>
          <button type="button" class="md-code-copy-btn" data-copy-code aria-label="${copyLabel}">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
            <span>${copyLabel}</span>
          </button>
        </div>
        <pre><code class="language-${langCode || 'text'}">${codeContent}</code></pre>
      </div>
    `;
  });

  return html;
}

// 2. Update Live Statistics
function updateStats(text) {
  const trimmed = text.trim();
  const words = trimmed ? (trimmed.match(/\S+/g) || []).length : 0;
  const chars = text.length;
  const lines = text ? text.split('\n').length : 0;
  const readTime = Math.max(1, Math.ceil(words / 200));

  const wordsEl = byId('mp-stat-words');
  const charsEl = byId('mp-stat-chars');
  const linesEl = byId('mp-stat-lines');
  const readTimeEl = byId('mp-stat-read-time');

  if (wordsEl) wordsEl.textContent = words.toLocaleString();
  if (charsEl) charsEl.textContent = chars.toLocaleString();
  if (linesEl) linesEl.textContent = lines.toLocaleString();
  if (readTimeEl) readTimeEl.textContent = isAr ? `~${readTime} دقيقة قراءة` : `~${readTime} min read`;
}

// 3. Render Markdown into Preview
function render() {
  if (!mpInput || !mpPreview) return;
  clearTimeout(timer);
  timer = setTimeout(() => {
    try {
      const rawText = mpInput.value;
      const transformed = transformMarkdown(rawText);
      const cleanHtml = DOMPurify.sanitize(transformed, PURIFY_CONFIG);
      mpPreview.innerHTML = cleanHtml;

      // Update statistics
      updateStats(rawText);

      // Autosave locally
      try {
        localStorage.setItem(STORAGE_KEY, rawText);
        const autoEl = byId('mp-autosave-status');
        if (autoEl) autoEl.textContent = isAr ? 'محفوظ محلياً' : 'Saved locally';
      } catch (e) {}

      if (hasStarted) {
        trackEvent('tool_completed', 'markdown-preview');
      }
    } catch (e) {
      mpPreview.textContent = isAr ? 'تعذرت معاينة Markdown.' : 'Markdown preview failed.';
      trackEvent('tool_failed', 'markdown-preview');
    }
  }, 60);
}

// 4. Interactive Task Checkbox Toggle in Markdown Source
function toggleTaskInText(text, targetIndex) {
  let currentIndex = 0;
  return text.replace(/^(\s*[-*+]\s+\[)([ xX])(\]\s+)/gm, (match, prefix, check, suffix) => {
    if (currentIndex === targetIndex) {
      currentIndex++;
      const newCheck = (check === ' ' ? 'x' : ' ');
      return `${prefix}${newCheck}${suffix}`;
    }
    currentIndex++;
    return match;
  });
}

// 5. Textarea Formatting Insertion Utility
function insertFormatting(action) {
  if (!mpInput) return;
  pushSnapshotBeforeChange();
  const start = mpInput.selectionStart;
  const end = mpInput.selectionEnd;
  const val = mpInput.value;
  const selected = val.substring(start, end);

  let replacement = '';
  let cursorOffset = 0;

  switch (action) {
    case 'h1':
      replacement = `\n# ${selected || (isAr ? 'عنوان رئيسي' : 'Heading 1')}\n`;
      break;
    case 'h2':
      replacement = `\n## ${selected || (isAr ? 'عنوان فرعي' : 'Heading 2')}\n`;
      break;
    case 'h3':
      replacement = `\n### ${selected || (isAr ? 'عنوان قسم' : 'Heading 3')}\n`;
      break;
    case 'bold':
      replacement = `**${selected || (isAr ? 'نص عريض' : 'Bold text')}**`;
      cursorOffset = selected ? 0 : 2;
      break;
    case 'italic':
      replacement = `*${selected || (isAr ? 'نص مائل' : 'Italic text')}*`;
      cursorOffset = selected ? 0 : 1;
      break;
    case 'strike':
      replacement = `~~${selected || (isAr ? 'نص مشطوب' : 'Strikethrough')}~~`;
      break;
    case 'highlight':
      replacement = `==${selected || (isAr ? 'نص مميز' : 'Highlighted text')}==`;
      break;
    case 'code-inline':
      replacement = `\`${selected || (isAr ? 'كود' : 'code')}\``;
      break;
    case 'code-block':
      replacement = `\n\`\`\`javascript\n${selected || '// code here'}\n\`\`\`\n`;
      break;
    case 'quote':
      replacement = `\n> ${selected || (isAr ? 'اقتباس مميز' : 'Blockquote')}\n`;
      break;
    case 'ul':
      replacement = `\n- ${selected || (isAr ? 'عنصر قائمة' : 'List item')}\n`;
      break;
    case 'ol':
      replacement = `\n1. ${selected || (isAr ? 'عنصر مرقم' : 'Numbered item')}\n`;
      break;
    case 'task':
      replacement = `\n- [ ] ${selected || (isAr ? 'مهمة جديدة' : 'New task')}\n`;
      break;
    case 'link':
      replacement = `[${selected || (isAr ? 'عنوان الرابط' : 'Link title')}](https://example.com)`;
      break;
    case 'image':
      replacement = `![${selected || (isAr ? 'وصف الصورة' : 'Image alt')}](https://example.com/image.png)`;
      break;
    case 'table':
      replacement = isAr
        ? `\n\n| العمود 1 | العمود 2 | العمود 3 |\n| :--- | :---: | ---: |\n| عنصر 1 | عنصر 2 | عنصر 3 |\n| عنصر 4 | عنصر 5 | عنصر 6 |\n\n`
        : `\n\n| Column 1 | Column 2 | Column 3 |\n| :--- | :---: | ---: |\n| Item 1 | Item 2 | Item 3 |\n| Item 4 | Item 5 | Item 6 |\n\n`;
      break;
    case 'hr':
      replacement = `\n\n---\n\n`;
      break;
    case 'alert-note':
      replacement = `\n> [!NOTE]\n> ${selected || (isAr ? 'اكتب الملاحظة هنا...' : 'Write note here...')}\n\n`;
      break;
    case 'alert-tip':
      replacement = `\n> [!TIP]\n> ${selected || (isAr ? 'اكتب التلميح الذكي هنا...' : 'Write tip here...')}\n\n`;
      break;
    case 'alert-important':
      replacement = `\n> [!IMPORTANT]\n> ${selected || (isAr ? 'اكتب التنبيه الهام هنا...' : 'Write important notice here...')}\n\n`;
      break;
    case 'alert-warning':
      replacement = `\n> [!WARNING]\n> ${selected || (isAr ? 'اكتب التحذير هنا...' : 'Write warning here...')}\n\n`;
      break;
    case 'alert-caution':
      replacement = `\n> [!CAUTION]\n> ${selected || (isAr ? 'اكتب التنبيه الشديد هنا...' : 'Write caution here...')}\n\n`;
      break;
    default:
      return;
  }

  mpInput.focus();
  mpInput.setRangeText(replacement, start, end, 'select');
  render();
}

// 6. Collapsible Editor System
function setEditorCollapsed(collapsed) {
  if (!paneEditor || !app) return;
  const isCollapsed = Boolean(collapsed);
  paneEditor.classList.toggle('is-collapsed', isCollapsed);
  app.classList.toggle('editor-collapsed', isCollapsed);

  if (btnCollapseEditor) {
    const title = isCollapsed
      ? (isAr ? 'توسيع المحرر' : 'Expand Editor')
      : (isAr ? 'طي المحرر' : 'Collapse Editor');
    btnCollapseEditor.setAttribute('title', title);
    btnCollapseEditor.setAttribute('aria-label', title);
    const collapseText = btnCollapseEditor.querySelector('.mp-collapse-text');
    if (collapseText) {
      collapseText.textContent = title;
    }
  }
}

function toggleEditorCollapse() {
  const isCurrentlyCollapsed = paneEditor?.classList.contains('is-collapsed');
  setEditorCollapsed(!isCurrentlyCollapsed);
}

if (btnCollapseEditor) {
  btnCollapseEditor.addEventListener('click', (e) => {
    e.stopPropagation();
    toggleEditorCollapse();
  });
}

if (paneEditor) {
  paneEditor.addEventListener('click', (e) => {
    if (paneEditor.classList.contains('is-collapsed')) {
      setEditorCollapsed(false);
    }
  });
}

// 7. View Modes (Split / Editor Only / Preview Only)
function setViewMode(mode) {
  if (!app) return;
  app.classList.remove('view-split', 'view-editor', 'view-preview');
  app.classList.add(`view-${mode}`);

  // When switching explicitly to split or editor mode, restore editor if collapsed
  if (mode === 'split' || mode === 'editor') {
    setEditorCollapsed(false);
  }

  const btns = document.querySelectorAll('.mp-view-btn');
  btns.forEach(btn => {
    btn.classList.toggle('is-active', btn.dataset.view === mode);
  });

  try {
    localStorage.setItem(STORAGE_VIEW_KEY, mode);
  } catch (e) {}
}

// 8. Wide Mode Toggle
function toggleWideMode() {
  const toolPage = document.querySelector('.tool-page');
  const isWide = !toolPage?.classList.contains('is-wide');
  if (toolPage) toolPage.classList.toggle('is-wide', isWide);
  if (app) app.classList.toggle('mp-wide-mode', isWide);

  if (mpToggleWide) {
    const label = mpToggleWide.querySelector('.mp-action-label');
    if (label) {
      label.textContent = isWide
        ? (isAr ? 'العرض العادي' : 'Standard Width')
        : (isAr ? 'توسيع العرض' : 'Wide View');
    }
  }

  try {
    localStorage.setItem(STORAGE_WIDE_KEY, isWide ? '1' : '0');
  } catch (e) {}
}

// 9. Fullscreen Mode Toggle & Screen Isolation
function exitFullscreenMode() {
  if (!app) return;
  app.classList.remove('mp-fullscreen-mode');
  document.body.classList.remove('is-mp-fullscreen');
  document.documentElement.classList.remove('is-mp-fullscreen');

  // Restore editor back to expanded when leaving fullscreen
  setEditorCollapsed(false);

  if (mpToggleFullscreen) {
    const label = mpToggleFullscreen.querySelector('.mp-action-label');
    if (label) {
      label.textContent = isAr ? 'ملء الشاشة' : 'Fullscreen';
    }
  }
}

function toggleFullscreen() {
  if (!app) return;
  const isFs = !app.classList.contains('mp-fullscreen-mode');

  if (isFs) {
    app.classList.add('mp-fullscreen-mode');
    document.body.classList.add('is-mp-fullscreen');
    document.documentElement.classList.add('is-mp-fullscreen');

    // Automatically fold editor to the side in fullscreen as requested
    setEditorCollapsed(true);

    // Call requestFullscreen directly on app to avoid capturing the whole document
    if (app.requestFullscreen && !document.fullscreenElement) {
      app.requestFullscreen().catch(() => {});
    }
  } else {
    if (document.fullscreenElement && document.exitFullscreen) {
      document.exitFullscreen().catch(() => {});
    }
    exitFullscreenMode();
  }

  if (mpToggleFullscreen) {
    const label = mpToggleFullscreen.querySelector('.mp-action-label');
    if (label) {
      label.textContent = isFs
        ? (isAr ? 'إنهاء ملء الشاشة' : 'Exit Fullscreen')
        : (isAr ? 'ملء الشاشة' : 'Fullscreen');
    }
  }
}

// Exit fullscreen on Escape key or native fullscreen change
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && app?.classList.contains('mp-fullscreen-mode')) {
    if (document.fullscreenElement && document.exitFullscreen) {
      document.exitFullscreen().catch(() => {});
    }
    exitFullscreenMode();
  }
});
document.addEventListener('fullscreenchange', () => {
  if (!document.fullscreenElement && app?.classList.contains('mp-fullscreen-mode')) {
    exitFullscreenMode();
  }
});

// 9. Synchronized Scrolling
if (mpInput && mpPreview) {
  mpInput.addEventListener('scroll', () => {
    if (!mpSyncScroll?.checked || isScrolling) return;
    isScrolling = true;
    const pct = mpInput.scrollTop / (mpInput.scrollHeight - mpInput.clientHeight || 1);
    mpPreview.scrollTop = pct * (mpPreview.scrollHeight - mpPreview.clientHeight || 1);
    setTimeout(() => { isScrolling = false; }, 30);
  });

  mpPreview.addEventListener('scroll', () => {
    if (!mpSyncScroll?.checked || isScrolling) return;
    isScrolling = true;
    const pct = mpPreview.scrollTop / (mpPreview.scrollHeight - mpPreview.clientHeight || 1);
    mpInput.scrollTop = pct * (mpInput.scrollHeight - mpInput.clientHeight || 1);
    setTimeout(() => { isScrolling = false; }, 30);
  });
}

// 10. Interactive Task Checklist and Code Copy Event Delegation in Preview
if (mpPreview) {
  mpPreview.addEventListener('click', async (e) => {
    // 10a. Clicked on a Task List Checkbox
    const checkbox = e.target.closest('input[type="checkbox"]');
    if (checkbox) {
      const allCheckboxes = Array.from(mpPreview.querySelectorAll('input[type="checkbox"]'));
      const index = allCheckboxes.indexOf(checkbox);
      if (index !== -1 && mpInput) {
        mpInput.value = toggleTaskInText(mpInput.value, index);
        render();
      }
      return;
    }

    // 10b. Clicked on a Code Block Copy Button
    const copyBtn = e.target.closest('[data-copy-code]');
    if (copyBtn) {
      const card = copyBtn.closest('.md-code-card');
      const codeEl = card?.querySelector('pre code');
      if (codeEl) {
        try {
          await copyText(codeEl.textContent);
          const originalText = copyBtn.querySelector('span')?.textContent || '';
          const copyLabel = copyBtn.querySelector('span');
          if (copyLabel) copyLabel.textContent = isAr ? 'تم النسخ!' : 'Copied!';
          setTimeout(() => {
            if (copyLabel) copyLabel.textContent = originalText;
          }, 1800);
        } catch (err) {
          message('mp-status', isAr ? 'تعذر نسخ الكود.' : 'Code copy failed.', 'error');
        }
      }
    }
  });
}

// 11. Toolbar Action Delegation
const toolbar = document.querySelector('.mp-toolbar');
if (toolbar) {
  toolbar.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-action]');
    if (btn) {
      const action = btn.dataset.action;
      insertFormatting(action);
      if (mpAlertDropdown) mpAlertDropdown.hidden = true;
    }
  });
}

// 12. Alert Callouts Dropdown Menu Toggle
if (mpAlertMenuBtn && mpAlertDropdown) {
  mpAlertMenuBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    mpAlertDropdown.hidden = !mpAlertDropdown.hidden;
  });
  document.addEventListener('click', (e) => {
    if (!mpAlertDropdown.hidden && !e.target.closest('.mp-dropdown-wrap')) {
      mpAlertDropdown.hidden = true;
    }
  });
}

// 13. View Toggles Listener
const viewToggleGroup = document.querySelector('.mp-view-toggles');
if (viewToggleGroup) {
  viewToggleGroup.addEventListener('click', (e) => {
    const btn = e.target.closest('.mp-view-btn');
    if (btn && btn.dataset.view) {
      setViewMode(btn.dataset.view);
    }
  });
}

// 14. Action Buttons (Wide Mode, Fullscreen, Sample, Clear, Dir)
if (mpToggleWide) mpToggleWide.addEventListener('click', toggleWideMode);
if (mpToggleFullscreen) mpToggleFullscreen.addEventListener('click', toggleFullscreen);

let savedBeforeSampleText = null;
const btnSample = byId('mp-btn-sample');
if (btnSample && mpInput) {
  btnSample.addEventListener('click', () => {
    const sampleText = isAr ? SAMPLE_MD_AR : SAMPLE_MD_EN;
    const sampleLabel = btnSample.querySelector('span');

    // If currently displaying sample and we have saved previous text, revert back!
    if (savedBeforeSampleText !== null && mpInput.value.trim() === sampleText.trim()) {
      pushSnapshotBeforeChange();
      mpInput.value = savedBeforeSampleText;
      savedBeforeSampleText = null;
      render();
      if (sampleLabel) sampleLabel.textContent = isAr ? 'نموذج تجريبي' : 'Sample';
      btnSample.classList.remove('is-active');
      btnSample.setAttribute('title', isAr ? 'نموذج تجريبي' : 'Sample Template');
      message('mp-status', isAr ? 'تمت استعادة النص السابق بنجاح.' : 'Previous text restored.', 'success');
      return;
    }

    // Otherwise, save current custom text before loading sample
    if (mpInput.value.trim() !== sampleText.trim() && mpInput.value.trim() !== '') {
      savedBeforeSampleText = mpInput.value;
    }
    pushSnapshotBeforeChange();
    mpInput.value = sampleText;
    render();
    btnSample.classList.add('is-active');
    if (sampleLabel) sampleLabel.textContent = isAr ? 'استعادة النص' : 'Restore Text';
    btnSample.setAttribute('title', isAr ? 'اضغط لاستعادة النص السابق أو اضغط Ctrl+Z' : 'Click to restore previous text or press Ctrl+Z');
    message('mp-status', isAr ? 'تم تحميل النموذج. اضغط مجدداً أو اضغط Ctrl+Z للاستعادة.' : 'Sample loaded. Click again or press Ctrl+Z to restore.', 'info');
  });
}

const btnClear = byId('mp-btn-clear');
if (btnClear && mpInput) {
  btnClear.addEventListener('click', async () => {
    const confirmed = await showConfirm(
      isAr ? 'هل تريد بالتأكيد مسح كافة المحتويات؟' : 'Clear all content?',
      {
        title: isAr ? 'مسح المحتوى' : 'Clear Content',
        confirmVariant: 'danger',
        confirmText: isAr ? 'مسح' : 'Clear',
        cancelText: isAr ? 'إلغاء' : 'Cancel'
      }
    );
    if (confirmed) {
      pushSnapshotBeforeChange();
      mpInput.value = '';
      render();
      message('mp-status', isAr ? 'تم المسح (يمكنك التراجع بـ Ctrl+Z).' : 'Cleared (press Ctrl+Z to undo).', 'info');
    }
  });
}

const btnDir = byId('mp-btn-dir');
if (btnDir && mpInput) {
  btnDir.addEventListener('click', () => {
    const cur = mpInput.getAttribute('dir') || 'auto';
    const next = cur === 'rtl' ? 'ltr' : 'rtl';
    mpInput.setAttribute('dir', next);
  });
}

// 15. File Input Loader
if (mpFileInput && mpInput) {
  mpFileInput.addEventListener('change', (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      mpInput.value = evt.target?.result || '';
      render();
      message('mp-status', isAr ? `تم فتح ${file.name}` : `Opened ${file.name}`, 'success');
    };
    reader.readAsText(file, 'utf-8');
  });
}

// 16. Copy Markdown Button
const copyBtn = byId('mp-copy');
if (copyBtn && mpInput) {
  copyBtn.addEventListener('click', async () => {
    try {
      await copyText(mpInput.value);
      message('mp-status', isAr ? 'تم نسخ Markdown بنجاح.' : 'Markdown copied.', 'success');
      trackEvent('tool_completed', 'markdown-preview');
    } catch {
      message('mp-status', isAr ? 'تعذر النسخ.' : 'Copy failed.', 'error');
      trackEvent('tool_failed', 'markdown-preview');
    }
  });
}

// 17. Copy Clean HTML Button
const copyHtmlBtn = byId('mp-copy-html');
if (copyHtmlBtn && mpPreview) {
  copyHtmlBtn.addEventListener('click', async () => {
    try {
      await copyText(mpPreview.innerHTML);
      message('mp-status', isAr ? 'تم نسخ كود HTML بنجاح.' : 'HTML copied.', 'success');
      trackEvent('tool_completed', 'markdown-preview');
    } catch {
      message('mp-status', isAr ? 'تعذر نسخ HTML.' : 'HTML copy failed.', 'error');
      trackEvent('tool_failed', 'markdown-preview');
    }
  });
}

// 18. Download .md File
const downloadBtn = byId('mp-download');
if (downloadBtn && mpInput) {
  downloadBtn.addEventListener('click', () => {
    saveBlob(new Blob([mpInput.value], { type: 'text/markdown;charset=utf-8' }), 'document.md');
    message('mp-status', isAr ? 'تم تنزيل document.md' : 'Downloaded document.md', 'success');
    trackEvent('tool_completed', 'markdown-preview');
  });
}

// 19. Download Standalone HTML Document
const downloadHtmlBtn = byId('mp-download-html');
if (downloadHtmlBtn && mpPreview) {
  downloadHtmlBtn.addEventListener('click', () => {
    const titleMatch = mpInput?.value?.match(/^#\s+(.+)$/m);
    const docTitle = titleMatch ? titleMatch[1].trim() : (isAr ? 'مستند Markdown' : 'Markdown Document');
    const standaloneHtml = `<!DOCTYPE html>
<html lang="${isAr ? 'ar' : 'en'}" dir="${isAr ? 'rtl' : 'ltr'}">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${docTitle}</title>
  <style>
    :root { color-scheme: light dark; }
    body {
      font-family: system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif;
      max-width: 900px;
      margin: 40px auto;
      padding: 0 20px;
      line-height: 1.8;
      color: #1a202c;
      background: #ffffff;
    }
    @media (prefers-color-scheme: dark) {
      body { background: #0b0f17; color: #e2e8f0; }
      table th, table td { border-color: #334155; }
      thead { background: #1e293b; }
      blockquote { background: #1e293b; border-color: #3b82f6; }
      pre { background: #0d1117; }
    }
    h1, h2, h3 { border-bottom: 1px solid #e2e8f0; padding-bottom: 8px; }
    table { width: 100%; border-collapse: collapse; margin: 20px 0; }
    th, td { border: 1px solid #cbd5e1; padding: 10px 14px; }
    pre { padding: 16px; border-radius: 8px; overflow: auto; background: #f1f5f9; direction: ltr; }
    code { font-family: Consolas, monospace; }
    mark { background: #fde047; padding: 2px 4px; border-radius: 2px; }
    kbd { border: 1px solid #94a3b8; border-radius: 4px; padding: 2px 5px; font-size: 0.85em; }
    .md-code-copy-btn { display: none; }
    .md-alert { padding: 14px 18px; border-radius: 8px; margin: 16px 0; border-inline-start: 4px solid #3b82f6; background: rgba(59, 130, 246, 0.08); }
    .md-alert-tip { border-color: #10b981; background: rgba(16, 185, 129, 0.08); }
    .md-alert-warning { border-color: #f59e0b; background: rgba(245, 158, 11, 0.08); }
    .md-alert-caution { border-color: #ef4444; background: rgba(239, 68, 68, 0.08); }
    .md-alert-title { font-weight: bold; margin-bottom: 4px; }
  </style>
</head>
<body>
  ${mpPreview.innerHTML}
</body>
</html>`;

    saveBlob(new Blob([standaloneHtml], { type: 'text/html;charset=utf-8' }), 'document.html');
    message('mp-status', isAr ? 'تم تنزيل document.html' : 'Downloaded document.html', 'success');
    trackEvent('tool_completed', 'markdown-preview');
  });
}

// 20. Print Preview
const printBtn = byId('mp-print');
if (printBtn) {
  printBtn.addEventListener('click', () => {
    window.print();
  });
}

// 21. Keyboard Shortcuts & Input Event Listeners
if (mpInput) {
  mpInput.addEventListener('keydown', (e) => {
    const isCtrl = e.ctrlKey || e.metaKey;
    if (isCtrl && !e.altKey) {
      const key = e.key.toLowerCase();
      if (key === 'z' && !e.shiftKey) {
        e.preventDefault();
        doUndo();
        return;
      }
      if (key === 'y' || (key === 'z' && e.shiftKey)) {
        e.preventDefault();
        doRedo();
        return;
      }
    }
  });

  mpInput.addEventListener('input', () => {
    if (!hasStarted) {
      hasStarted = true;
      trackEvent('tool_started', 'markdown-preview');
    }
    recordHistorySnapshot();
    render();
  });
}

// Global keyboard shortcuts when focus is inside the markdown app
document.addEventListener('keydown', (e) => {
  if (document.activeElement === mpInput) return;
  if (!app?.contains(document.activeElement)) return;
  const isCtrl = e.ctrlKey || e.metaKey;
  if (isCtrl && !e.altKey) {
    const key = e.key.toLowerCase();
    if (key === 'z' && !e.shiftKey) {
      e.preventDefault();
      doUndo();
    } else if (key === 'y' || (key === 'z' && e.shiftKey)) {
      e.preventDefault();
      doRedo();
    }
  }
});

// 22. Initial Startup & Saved Content Restore
try {
  const savedContent = localStorage.getItem(STORAGE_KEY);
  if (savedContent && mpInput) {
    mpInput.value = savedContent;
  } else if (mpInput) {
    mpInput.value = isAr ? SAMPLE_MD_AR : SAMPLE_MD_EN;
  }

  const savedView = localStorage.getItem(STORAGE_VIEW_KEY);
  if (savedView && ['split', 'editor', 'preview'].includes(savedView)) {
    setViewMode(savedView);
  }

  const savedWide = localStorage.getItem(STORAGE_WIDE_KEY);
  if (savedWide === '1') {
    toggleWideMode();
  }
} catch (e) {
  if (mpInput) mpInput.value = isAr ? SAMPLE_MD_AR : SAMPLE_MD_EN;
}

if (mpInput) {
  lastRecordedValue = mpInput.value;
}
updateUndoRedoUI();

render();
